import { formatNumber, TooltipDirection, TooltipEntity } from '@enter/core';
import { mdiCheckboxBlankCircle } from '@mdi/js';
import { Icon } from '@mdi/react';
import * as d3 from 'd3';
import _ from 'lodash';
import { Granularity } from '../../models';
import React from 'react';
import * as S from './focus-group.styles';
import { IChartPoint, IChartTimeline } from './models';
import { createScales, getDateFormat, getInterval } from './util';

export interface IFocusGroupProps {
    chartRect: ClientRect;
    granularity: Granularity;
    timeline: IChartTimeline;
    labels: string[];
    x: number;
    y: number;
}
interface IFocusGroupState {
    granularity: Granularity;
    xScale: d3.ScaleTime<number, number>;
    yScale: d3.ScaleLinear<number, number>;
}

export class FocusGroup extends React.Component<IFocusGroupProps, IFocusGroupState> {
    private lineRef: React.RefObject<SVGLineElement>;
    constructor(props: IFocusGroupProps) {
        super(props);
        this.lineRef = React.createRef();
        const [xScale, yScale] = createScales(this.props.timeline, this.props.granularity, this.props.chartRect);
        this.state = {
            granularity: this.props.granularity,
            xScale,
            yScale,
        };
    }
    public componentDidUpdate(): void {
        const [xScale, yScale] = createScales(this.props.timeline, this.props.granularity, this.props.chartRect);
        const granularityChanged = this.props.granularity !== this.state.granularity;
        const xRangeChanged = !_.isEqual(xScale.range(), this.state.xScale.range());
        const yRangeChanged = !_.isEqual(yScale.range(), this.state.yScale.range());
        if (granularityChanged || xRangeChanged || yRangeChanged) {
            this.setState({
                granularity: this.props.granularity,
                xScale,
                yScale,
            });
        }
    }
    public render(): React.ReactElement {
        return (
            <>
                {this.renderFocusLine()}
                {this.renderTooltip()}
            </>
        );
    }
    private readonly renderFocusLine = (): React.ReactElement | undefined => {
        const { timeline } = this.props;
        const [tooltipX] = this.getTooltipPosition();
        if (tooltipX > -1 && timeline) {
            const { granularity, xScale, yScale } = this.state;
            const [maxY, minY] = yScale.range();
            if (this.isValidTooltipX(tooltipX, granularity, xScale)) {
                const date: Date = xScale.invert(tooltipX);
                const points = _.compact(
                    timeline.data.map((p: IChartPoint[]) => _.find<IChartPoint>(p, { date })),
                );
                const innerCircles = points.map((p: IChartPoint, i: number) => {
                    return (
                        <S.FocusInnerDot key={`i-${i}-${p.amount}`} ord={i} cx={tooltipX} cy={yScale(p.amount)} r={3} />
                    );
                });
                const outerCircles = points.map((p: IChartPoint, i: number) => {
                    return (
                        <S.FocusOuterDot key={`o-${i}-${p.amount}`} ord={i} cx={tooltipX} cy={yScale(p.amount)} r={5} />
                    );
                });
                // NB: split into segments to mount the tooltip correctly
                return (
                    <S.FocusLine>
                        <S.FocusSegment x1={tooltipX} x2={tooltipX} y1={minY} y2={(maxY - minY) / 2} />
                        <S.FocusSegment
                            ref={this.lineRef}
                            x1={tooltipX}
                            x2={tooltipX}
                            y1={(maxY - minY) / 2}
                            y2={maxY}
                        />
                        {innerCircles}
                        {outerCircles}
                    </S.FocusLine>
                );
            }
        }
    };
    private readonly renderTooltip = (): React.ReactElement | undefined => {
        const { labels, timeline } = this.props;
        const { granularity, xScale } = this.state;
        const [tooltipX, tooltipDirection] = this.getTooltipPosition();
        if (tooltipX > -1 && timeline) {
            const date: Date = xScale.invert(tooltipX);
            const dateFmt = getDateFormat(granularity, true);
            const points = timeline.data.map((datum: IChartPoint[], i: number) =>
                _.find<IChartPoint>(datum, {
                    date,
                }),
            );
            const orderedPoints = _.compact(_.orderBy(points, 'amount', 'desc'));
            const rows = _.map(orderedPoints, point => {
                // find index in original list to preserve colors/labels
                const i = _.indexOf(points, point);
                return (
                    <S.TooltipRow key={`${i}-${point.amount}`}>
                        <S.TooltipIcon ord={i}>
                            <Icon path={mdiCheckboxBlankCircle} />
                        </S.TooltipIcon>
                        <S.TooltipTitle>{labels[i]}</S.TooltipTitle>
                        <S.TooltipValue>{formatNumber('Currency')(point.amount)}</S.TooltipValue>
                    </S.TooltipRow>
                );
            });
            return (
                <TooltipEntity open={tooltipX > -1} direction={tooltipDirection} anchor={this.lineRef}>
                    <S.Tooltip>
                        <S.TooltipRow>
                            <S.TooltipDate>{dateFmt(date)}</S.TooltipDate>
                        </S.TooltipRow>
                        {rows}
                    </S.Tooltip>
                </TooltipEntity>
            );
        }
    };
    // valid if current X position is a rounded interval point (axis tick)
    private readonly isValidTooltipX = (
        x: number,
        granularity: Granularity,
        xScale: d3.ScaleTime<number, number>,
    ): boolean => {
        const interval = getInterval(granularity);
        const date = interval.round(xScale.invert(x));
        return xScale(date) === x;
    };
    private readonly getTooltipPosition = (): [number, TooltipDirection] => {
        const { granularity, xScale } = this.state;
        const interval = getInterval(granularity);
        const date = interval.round(xScale.invert(this.props.x));
        const [minX, maxX] = xScale.range();
        let tooltipX = xScale(date);
        if (tooltipX < minX || maxX < tooltipX) {
            tooltipX = -1;
        }
        // NB: also limit by mouse position
        const {
            chartRect: { top, right, bottom, left },
            x,
            y,
        } = this.props;
        if (x < left || x > right || y < top || y > bottom) {
            tooltipX = -1;
        }

        const tooltipDirection = tooltipX - minX > maxX - tooltipX ? 'left' : 'right';
        return [tooltipX, tooltipDirection];
    };
}
